/**
 * Route admin `/tutor/admin/etablissements` — « Établissements ».
 *
 * État de l'annuaire (collèges, lycées, EREA ouverts, fermés, dernier import, élèves rattachés) et mise à
 * jour depuis l'Annuaire de l'éducation par l'edge function `import-schools` : « Aperçu » (télécharge et
 * compte, n'écrit rien) puis « Mettre à jour » (upsert par UAI ; les absents de l'annuaire sont fermés).
 */

import { useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, RefreshCw, School, Search } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  importResultLines,
  nb,
  useRunSchoolsImport,
  useSchoolsDirectoryStats,
  type SchoolsImportMode,
  type SchoolsImportResult,
} from '@/lib/admin/schools-import';

const btn =
  'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-xl px-4 text-sm font-semibold transition-colors disabled:opacity-50';
const primaryBtn = cn(btn, 'bg-primary-600 hover:bg-primary-700 text-white');
const secondaryBtn = cn(btn, 'border-ink-200 text-ink-700 hover:bg-ink-50 border bg-white');
const card = 'border-ink-200 rounded-2xl border bg-white p-4';

const dateFr = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-ink-50 rounded-xl px-3 py-2">
      <div className="font-display text-ink-900 text-[20px] font-extrabold tabular-nums">
        {value}
      </div>
      <div className="text-ink-600 text-[12px]">{label}</div>
    </div>
  );
}

export function TutorAdminSchoolsPage() {
  const stats = useSchoolsDirectoryStats();
  const run = useRunSchoolsImport();
  const [running, setRunning] = useState<SchoolsImportMode | null>(null);
  const [result, setResult] = useState<SchoolsImportResult | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const launch = (mode: SchoolsImportMode) => {
    setRunning(mode);
    setFailure(null);
    setResult(null);
    run
      .mutateAsync(mode)
      .then(setResult, (e: Error) => setFailure(e.message))
      .finally(() => setRunning(null));
  };

  const s = stats.data;
  const ok = result?.ok === true;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 pb-16 sm:p-6">
      <header>
        <h1 className="font-display text-ink-900 flex items-center gap-2 text-[22px] font-extrabold">
          <School className="text-primary-600 h-6 w-6" aria-hidden />
          Établissements
        </h1>
        <p className="text-ink-600 mt-1 text-[14px]">
          L’annuaire où chaque élève choisit son collège ou son lycée (métropole, DOM et outre-mer),
          base des classements par établissement, ville et département. Source : Annuaire de
          l’éducation (data.education.gouv.fr, Licence Ouverte).
        </p>
      </header>

      <section aria-labelledby="etat" className={cn(card, 'space-y-3')}>
        <h2 id="etat" className="font-display text-ink-900 text-[17px] font-extrabold">
          État de l’annuaire
        </h2>
        {stats.isLoading ? (
          <p className="text-ink-500 flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Chargement…
          </p>
        ) : stats.isError || !s ? (
          <p role="alert" className="text-error-600 text-sm">
            Impossible de lire l’annuaire.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Stat label="ouverts" value={nb(s.open)} />
              <Stat label="collèges" value={nb(s.colleges)} />
              <Stat label="lycées" value={nb(s.lycees)} />
              <Stat label="EREA" value={nb(s.erea)} />
            </div>
            <ul className="text-ink-700 space-y-1 text-[13px]">
              <li>
                Dernière mise à jour :{' '}
                <strong>{s.lastImportAt ? dateFr(s.lastImportAt) : 'jamais'}</strong>
              </li>
              <li>{nb(s.closed)} fermés (plus proposés à la recherche, gardés par leurs élèves)</li>
              {s.studentsWithSchool != null && (
                <li>{nb(s.studentsWithSchool)} élèves ont choisi leur établissement</li>
              )}
            </ul>
          </>
        )}
      </section>

      <section aria-labelledby="maj" className={cn(card, 'space-y-3')}>
        <h2 id="maj" className="font-display text-ink-900 text-[17px] font-extrabold">
          Mettre à jour depuis l’annuaire
        </h2>
        <p className="text-ink-600 text-[13px]">
          Télécharge l’annuaire du jour (~14 900 sites, une minute environ). Les établissements sont
          mis à jour par leur UAI, sans doublon ; ceux qui ont disparu de l’annuaire sont fermés.
          L’aperçu compte sans rien écrire. Un export incomplet n’est jamais écrit.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={secondaryBtn}
            disabled={running != null}
            onClick={() => launch('preview')}
          >
            {running === 'preview' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Search className="h-4 w-4" aria-hidden />
            )}
            Aperçu
          </button>
          <button
            type="button"
            className={primaryBtn}
            disabled={running != null}
            onClick={() => launch('import')}
          >
            {running === 'import' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="h-4 w-4" aria-hidden />
            )}
            Mettre à jour
          </button>
        </div>

        {running && (
          <p role="status" className="text-ink-600 text-[13px]">
            {running === 'import'
              ? 'Téléchargement et écriture en cours… ne ferme pas la page.'
              : 'Téléchargement de l’annuaire…'}
          </p>
        )}

        {failure && (
          <p
            role="alert"
            className="bg-error-50 text-error-600 flex gap-2 rounded-xl px-3 py-2 text-[13px]"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {failure}
          </p>
        )}

        {result && (
          <div
            role={ok ? 'status' : 'alert'}
            data-testid="schools-import-result"
            className={cn(
              'space-y-1 rounded-xl px-3 py-2 text-[13px]',
              ok ? 'bg-success-50 text-ink-800' : 'bg-error-50 text-error-600',
            )}
          >
            <p className="flex items-center gap-1.5 font-semibold">
              {ok ? (
                <CheckCircle2 className="text-success-600 h-4 w-4" aria-hidden />
              ) : (
                <AlertCircle className="h-4 w-4" aria-hidden />
              )}
              {ok
                ? result.mode === 'import'
                  ? 'Annuaire mis à jour.'
                  : 'Aperçu : rien n’a été écrit.'
                : (result.message ?? 'Échec.')}
            </p>
            {importResultLines(result).map((l) => (
              <p key={l}>{l}</p>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
